import { ref } from 'vue';
import { AxiosError } from 'axios';
import { defineStore } from 'pinia';

import initApi from '@/api/initApiV2';

import {
  ControlApi,
  ProjectsApi,
  type Comment,
  type ControlApiQueryControlsExpandedRequest,
  type ControlExpandedPaginated,
  type FrameworkIcon,
  type Log,
  type PaginatedResultFramework,
  type PaginatedResultRequirementBase,
  type Project,
  type ProjectExpandedPaginated,
  type ProjectUserPaginated,
  type ProjectsApiAddProjectUserRoleRequest,
  type ProjectsApiAddProjectUserToProjectRequest,
  type ProjectsApiCreateProjectRequest,
  type ProjectsApiCreateProjectCommentRequest,
  type ProjectsApiCreateProjectFrameworksEndpointRequest,
  type ProjectsApiDeleteProjectRequest,
  type ProjectsApiGetProjectCommentsRequest,
  type ProjectsApiGetProjectFrameworksRequest,
  type ProjectsApiGetProjectLogsRequest,
  type ProjectsApiGetProjectRequest,
  type ProjectsApiGetProjectRequirementsRequest,
  type ProjectsApiGetProjectsExpandedRequest,
  type ProjectsApiGetProjectsRequest,
  type ProjectsApiGetProjectUsersRequest,
  type ProjectsApiListProjectUserRolesRequest,
  type ProjectsApiRemoveProjectUserRequest,
  type ProjectsApiRemoveProjectUserRoleRequest,
  type ProjectsApiUpdateProjectRegistrationStatusRequest,
  type ProjectsApiUpdateProjectRequest,
} from '@/clients/v2';

import { handleApiError } from '@/api/handleApiError.ts';
import type { RequestConfig } from '@/api/Request';

// Appends a page onto the accumulated result for infinite pagination, unioning
// `extra.frameworks` so icons resolve for rows from every loaded page.
function mergeProjectsPage(
  previous: ProjectExpandedPaginated | null,
  next: ProjectExpandedPaginated,
): ProjectExpandedPaginated {
  if (!previous) return next;

  const frameworksByCode = new Map<string, FrameworkIcon>();
  for (const framework of previous.extra?.frameworks ?? []) {
    frameworksByCode.set(framework.framework_code, framework);
  }
  for (const framework of next.extra?.frameworks ?? []) {
    frameworksByCode.set(framework.framework_code, framework);
  }

  return {
    ...next,
    items: [...previous.items, ...next.items],
    extra: { frameworks: [...frameworksByCode.values()] },
  };
}

export const useProjectsStoreV2 = defineStore('projects-v2', () => {
  const project = ref<Project | null>(null);
  const projectsData = ref<ProjectExpandedPaginated | null>(null);
  const projectRequirementsData = ref<PaginatedResultRequirementBase | null>(null);
  const projectControlsData = ref<ControlExpandedPaginated | null>(null);
  const projectFrameworks = ref<PaginatedResultFramework | null>(null);
  const projectUsersData = ref<ProjectUserPaginated | null>(null);
  const projectComments = ref<Comment[]>([]);
  const projectLogs = ref<Log[]>([]);

  const conceptsLoading = ref(false);

  const getProjects = async (
    params: ProjectsApiGetProjectsExpandedRequest,
    options?: RequestConfig,
    append: boolean = false,
  ) => {
    const projectsApi = await initApi(ProjectsApi);

    try {
      const response = await projectsApi?.getProjectsExpanded(params, options);

      if (!response) return;

      projectsData.value = append
        ? mergeProjectsPage(projectsData.value, response.data)
        : response.data;

      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  /** A page of projects for a picker; leaves the registry list in the store untouched. */
  const listProjects = async (params: ProjectsApiGetProjectsRequest) => {
    const projectsApi = await initApi(ProjectsApi);

    try {
      const response = await projectsApi?.getProjects(params);
      return response?.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const postProject = async (params: ProjectsApiCreateProjectRequest) => {
    const projectsApi = await initApi(ProjectsApi);

    try {
      const response = await projectsApi?.createProject(params);

      return response?.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const createProject = async (params: ProjectsApiCreateProjectRequest) => {
    const created = await postProject(params);

    if (created) project.value = created;

    return created;
  };

  // Navigating between AI Systems overlaps fetches; an earlier response must not overwrite a later
  // one (same guard as the comments and logs below).
  let projectRequestId = 0;

  const getProject = async (params: ProjectsApiGetProjectRequest) => {
    const requestId = ++projectRequestId;
    const projectsApi = await initApi(ProjectsApi);

    try {
      const response = await projectsApi?.getProject(params);

      if (!response || requestId !== projectRequestId) return;

      project.value = response.data;

      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const updateProject = async (params: ProjectsApiUpdateProjectRequest) => {
    const projectsApi = await initApi(ProjectsApi);

    try {
      const response = await projectsApi?.updateProject(params);

      if (!response) return;

      project.value = response.data;

      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const updateProjectRegistrationStatus = async (
    params: ProjectsApiUpdateProjectRegistrationStatusRequest,
  ) => {
    const projectsApi = await initApi(ProjectsApi);

    try {
      const response = await projectsApi?.updateProjectRegistrationStatus(params);

      if (!response) return;

      project.value = response.data;

      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const getProjectRequirements = async (params: ProjectsApiGetProjectRequirementsRequest) => {
    const projectsApi = await initApi(ProjectsApi);

    try {
      conceptsLoading.value = true;

      const response = await projectsApi?.getProjectRequirements(params);

      if (!response) return;

      projectRequirementsData.value = response.data;

      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    } finally {
      conceptsLoading.value = false;
    }
  };

  const getProjectControlsExpanded = async (params: ControlApiQueryControlsExpandedRequest) => {
    const controlApi = await initApi(ControlApi);

    try {
      conceptsLoading.value = true;

      const response = await controlApi?.queryControlsExpanded(params);

      if (!response) return;

      projectControlsData.value = response.data;

      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    } finally {
      conceptsLoading.value = false;
    }
  };

  const getProjectFrameworks = async (
    params: ProjectsApiGetProjectFrameworksRequest,
    options?: RequestConfig,
    updateStore: boolean = true,
  ) => {
    const projectsApi = await initApi(ProjectsApi);

    try {
      if (updateStore) {
        conceptsLoading.value = true;
      }

      const response = await projectsApi?.getProjectFrameworks(params, options);

      if (!response) return;

      if (updateStore) {
        projectFrameworks.value = response.data;
      }

      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    } finally {
      if (updateStore) {
        conceptsLoading.value = false;
      }
    }
  };

  const createProjectFrameworks = async (
    params: ProjectsApiCreateProjectFrameworksEndpointRequest,
  ) => {
    try {
      const projectsApi = await initApi(ProjectsApi);
      await projectsApi?.createProjectFrameworksEndpoint(params);
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const getProjectUsers = async (
    params: ProjectsApiGetProjectUsersRequest,
    updateStore: boolean = true,
    options?: RequestConfig,
  ) => {
    const projectsApi = await initApi(ProjectsApi);

    try {
      const response = await projectsApi?.getProjectUsers(params, options);

      if (!response) return;

      if (updateStore) {
        projectUsersData.value = response.data;
      }

      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const addProjectUserRole = async (params: ProjectsApiAddProjectUserRoleRequest) => {
    const projectsApi = await initApi(ProjectsApi);

    try {
      const response = await projectsApi?.addProjectUserRole(params);

      if (!response) return;

      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const addProjectUserToProject = async (params: ProjectsApiAddProjectUserToProjectRequest) => {
    const projectsApi = await initApi(ProjectsApi);

    try {
      const response = await projectsApi?.addProjectUserToProject(params);

      if (!response) return;

      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const removeProjectUser = async (params: ProjectsApiRemoveProjectUserRequest) => {
    const projectsApi = await initApi(ProjectsApi);

    try {
      await projectsApi?.removeProjectUser(params);
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const removeProjectUserRole = async (params: ProjectsApiRemoveProjectUserRoleRequest) => {
    const projectsApi = await initApi(ProjectsApi);

    try {
      await projectsApi?.removeProjectUserRole(params);
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const listProjectUserRoles = async (params: ProjectsApiListProjectUserRolesRequest) => {
    const projectsApi = await initApi(ProjectsApi);

    try {
      const response = await projectsApi?.listProjectUserRoles(params);

      if (!response) return;

      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const deleteProject = async (params: ProjectsApiDeleteProjectRequest) => {
    try {
      const projectsApi = await initApi(ProjectsApi);
      await projectsApi?.deleteProject(params);
      resetStore();
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  // Comments and logs are shared singleton arrays: a response may only land if it belongs
  // to the latest request, and a failed latest request clears instead of showing the
  // previous system's data. Resetting bumps the counters so in-flight responses land nowhere.
  let commentsRequestId = 0;
  let logsRequestId = 0;

  const resetStore = () => {
    projectRequestId += 1;
    project.value = null;
    projectsData.value = null;
    projectFrameworks.value = null;
    projectRequirementsData.value = null;
    projectUsersData.value = null;
    projectComments.value = [];
    projectLogs.value = [];
    commentsRequestId += 1;
    logsRequestId += 1;
    conceptsLoading.value = false;
  };

  const getProjectComments = async (params: ProjectsApiGetProjectCommentsRequest) => {
    const requestId = ++commentsRequestId;
    try {
      const projectsApi = await initApi(ProjectsApi);
      const response = await projectsApi?.getProjectComments(params);
      if (!response || requestId !== commentsRequestId) return;

      projectComments.value = response.data;
    } catch (error) {
      if (requestId === commentsRequestId) projectComments.value = [];
      handleApiError(error as AxiosError);
    }
  };

  const createProjectComment = async (params: ProjectsApiCreateProjectCommentRequest) => {
    try {
      const projectsApi = await initApi(ProjectsApi);
      const response = await projectsApi?.createProjectComment(params);

      return response?.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const getProjectLogs = async (params: ProjectsApiGetProjectLogsRequest) => {
    const requestId = ++logsRequestId;
    try {
      const projectsApi = await initApi(ProjectsApi);
      const response = await projectsApi?.getProjectLogs(params);
      if (!response || requestId !== logsRequestId) return;

      projectLogs.value = response.data;
    } catch (error) {
      if (requestId === logsRequestId) projectLogs.value = [];
      handleApiError(error as AxiosError);
    }
  };

  return {
    project,
    projectsData,
    projectFrameworks,
    projectRequirementsData,
    projectControlsData,
    projectUsersData,
    projectComments,
    projectLogs,
    conceptsLoading,

    getProjects,
    listProjects,
    getProjectComments,
    createProjectComment,
    getProjectLogs,
    createProject,
    getProject,
    updateProject,
    updateProjectRegistrationStatus,
    deleteProject,
    getProjectRequirements,
    getProjectControlsExpanded,
    getProjectFrameworks,
    createProjectFrameworks,
    getProjectUsers,
    addProjectUserRole,
    addProjectUserToProject,
    removeProjectUser,
    removeProjectUserRole,
    listProjectUserRoles,
    resetStore,
  };
});
